import React, { useEffect } from "react";
import Plot from "react-plotly.js";

import { useError } from "../../../contexts/ErrorContext";
import { useExportFTQ } from "../../../hooks/useFulfillmentsAnalytics";

import { Box, CircularProgress } from "@mui/material";
import { useTheme } from "@mui/material/styles";

interface ExportsFTQChartProps {
  dateStart?: string;
  dateEnd?: string;
}

const ExportsFTQChart: React.FC<ExportsFTQChartProps> = (props) => {
  const { showError } = useError();
  const theme = useTheme();
  const primaryColor = theme.palette.primary.main;

  const { dateStart = undefined, dateEnd = undefined } = props;

  const {
    data: exportFTQData,
    error,
    isLoading,
  } = useExportFTQ(dateStart, dateEnd);

  useEffect(() => {
    if (error) {
      showError(error?.message || `Failed to get exports FTQ`);
    }
  }, [error, showError]);

  return (
    <Box
      sx={{
        display: "block",
        width: "100%",
        minHeight: "500px",
      }}
    >
      {isLoading ? (
        <CircularProgress
          size={20}
          sx={{ marginRight: 1, verticalAlign: "middle" }}
        />
      ) : exportFTQData && exportFTQData.length > 0 ? (
        <Plot
          config={{
            displaylogo: false,
            modeBarButtonsToRemove: ["select2d", "lasso2d"],
            responsive: true,
          }}
          data={[
            {
              x: exportFTQData.map((item) => {
                const date = new Date(item.week_start_date);
                return date.toISOString().split("T")[0];
              }),
              y: exportFTQData.map((item) => item.count_scheduled_deliveries),
              type: "bar",
              name: "Total Deliveries",
              marker: { color: "#2A9D8F" },
              hovertemplate: `<b>Total Deliveries:</b> %{y}<extra></extra>`,
            },
            {
              x: exportFTQData.map((item) => {
                const date = new Date(item.week_start_date);
                return date.toISOString().split("T")[0];
              }),
              y: exportFTQData.map((item) => item.pct_export_ftq_succeeded),
              type: "scatter",
              mode: "lines+markers",
              name: "FTQ Success Rate (%)",
              marker: { color: "#E76F51", size: 4 },
              line: { color: "#E76F51", width: 2 },
              yaxis: "y2",
              hoverinfo: "none",
              hovertemplate: `<b>FTQ Success Rate:</b> %{y}%<br><b>FTQ Success Count:</b> %{customdata[0]}<extra></extra>`,
              customdata: exportFTQData.map((item) => [
                item.count_export_ftq_succeeded,
              ]),
            },
          ]}
          layout={{
            title: {
              text: "Export FTQ (First Time Quality)",
              font: {
                color: primaryColor,
              },
            },
            legend: {
              orientation: "h",
              x: 0.5,
              y: 1.1,
              xanchor: "center",
              yanchor: "top",
            },
            xaxis: {
              title: {
                text: "Week Start Date",
                standoff: 15,
              },
              showgrid: false,
              tickangle: -45,
              tickfont: {
                size: 12,
              },
            },
            yaxis: {
              title: "Total Deliveries",
              showgrid: true,
              gridcolor: "#D3D3D3",
              rangemode: "tozero",
            },
            yaxis2: {
              title: "FTQ Success Rate (%)",
              overlaying: "y",
              side: "right",
              showgrid: false,
              autorange: false,
              range: [0, 105],
            },
            hovermode: "x unified",
            barmode: "group",
            margin: {
              b: 100,
              t: 80,
              l: 60,
              r: 60,
            },
            hoverlabel: {
              bgcolor: "#FFF",
              font: { color: "#264653" },
              bordercolor: "#E76F51",
              align: "left",
            },
            font: {
              family: '"Noto Sans", serif',
              size: 14,
            },
          }}
          style={{ width: "100%", height: "100%" }}
        />
      ) : (
        <p>No data available</p>
      )}
    </Box>
  );
};

export default ExportsFTQChart;
